/**
 * /account-recovery/2fa — відновлення двофакторного входу через службу
 * підтримки (ч.1091).
 *
 * Резервних кодів у системі немає (рішення власника). Людина, що втратила
 * телефон із застосунком або Telegram, завантажує лист, підписаний КЕП однієї
 * з організацій свого бізнес-акаунта (.p7s). Запит потрапляє оператору;
 * розгляд — до 72 годин у робочі дні; результат — листом на email облікового
 * запису. Відповідь сторінки однакова для будь-якого email (не видає, чи
 * існує обліковий запис).
 */
import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { requestTwoFactorReset } from '@/api/auth';
import { extractApiError } from '@/utils/extractApiError';

export const REVIEW_HOURS = 72;

const muted = { color: 'var(--text-secondary)', fontSize: '0.9rem' } as const;

export default function TwoFactorRecoveryPage() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [comment, setComment] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!file) return;
    setError(null);
    setLoading(true);
    try {
      await requestTwoFactorReset(email.trim(), file, comment.trim());
      setSentTo(email.trim());
    } catch (err) {
      setError(extractApiError(err, 'Не вдалося надіслати запит. Спробуйте пізніше.'));
    } finally {
      setLoading(false);
    }
  }

  if (sentTo) {
    return (
      <div className="login">
        <form onSubmit={(e) => e.preventDefault()}>
          <h1>Запит надіслано</h1>
          <p style={muted}>
            Якщо обліковий запис <strong>{sentTo}</strong> існує і в ньому увімкнено
            двофакторний вхід, запит зареєстровано — подробиці надіслано на цю адресу
            (і в Telegram, якщо його підключено).
          </p>
          <p style={muted}>
            Оператор служби підтримки перевірить лист і скине двофакторний вхід.
            Розгляд може тривати <strong>до {REVIEW_HOURS} годин у робочі дні</strong>.
            Після скидання увійдіть з паролем — код система не питатиме — і одразу
            налаштуйте двофакторний вхід знову.
          </p>
          <p style={{ marginTop: '1rem', fontSize: '0.85rem', textAlign: 'center' }}>
            <Link to="/login">Повернутися до входу</Link>
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className="login">
      <form onSubmit={onSubmit} style={{ width: 460 }}>
        <h1>Відновлення двофакторного входу</h1>
        <p style={{ ...muted, marginTop: 0 }}>
          Немає доступу до коду (втрачено телефон із застосунком або Telegram)?
          Двофакторний вхід скидає служба підтримки за <strong>листом, підписаним
          КЕП однієї з організацій вашого бізнес-акаунта</strong>.
        </p>
        <ol style={{ ...muted, paddingLeft: '1.2rem', margin: '0 0 1rem' }}>
          <li>
            Напишіть лист у довільній формі: «Прошу скинути двофакторний вхід до
            облікового запису &lt;ваш email&gt;».
          </li>
          <li>
            Підпишіть його КЕП організації або її керівника (для ФОП — ключем ФОП):
            «Дія.Підпис», id.gov.ua → «Підписати файл» або «ІІТ Користувач ЦСК».
            Результат — файл <strong>.p7s</strong>.
          </li>
          <li>Додайте файл .p7s нижче.</li>
        </ol>
        <div className="field">
          <label>Email облікового запису</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            autoComplete="email"
            autoFocus
            required
          />
        </div>
        <div className="field">
          <label htmlFor="tfa-letter">Лист із КЕП-підписом (.p7s, до 5 МБ)</label>
          <input
            id="tfa-letter"
            type="file"
            accept=".p7s,application/pkcs7-signature"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>
        <div className="field">
          <label>Коментар (необовʼязково)</label>
          <textarea
            value={comment}
            rows={2}
            maxLength={2000}
            placeholder="наприклад, «загубив телефон із застосунком»"
            onChange={(e) => setComment(e.target.value)}
          />
        </div>
        <button type="submit" className="primary" style={{ width: '100%' }}
          disabled={loading || !email.trim() || !file}>
          {loading ? 'надсилання…' : 'надіслати запит'}
        </button>
        {error && <div className="error">{error}</div>}
        <p style={{ ...muted, fontSize: '0.8rem', marginTop: '0.75rem' }}>
          Розгляд — до {REVIEW_HOURS} годин у робочі дні; результат надійде листом
          на email облікового запису. Після скидання всі попередні сесії буде
          завершено.
        </p>
        <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', textAlign: 'center' }}>
          <Link to="/help/account-recovery-2fa">Докладніше в довідці</Link>
          {' · '}
          <Link to="/login">Повернутися до входу</Link>
        </p>
      </form>
    </div>
  );
}
